import { useState, useCallback } from 'react';

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  variant: 'danger' | 'warning' | 'info';
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmOptions {
  title: string;
  message: string;
  variant?: 'danger' | 'warning' | 'info';
  confirmLabel?: string;
  cancelLabel?: string;
}

const initialState: ConfirmState = {
  open: false,
  title: '',
  message: '',
  variant: 'danger',
};

export default function useConfirm() {
  const [state, setState] = useState<ConfirmState>(initialState);
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({
        open: true,
        title: options.title,
        message: options.message,
        variant: options.variant ?? 'danger',
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
      });
      setResolveRef(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef?.(true);
    setState(initialState);
    setResolveRef(null);
  }, [resolveRef]);

  const handleCancel = useCallback(() => {
    resolveRef?.(false);
    setState(initialState);
    setResolveRef(null);
  }, [resolveRef]);

  return {
    confirm,
    confirmProps: {
      ...state,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
}
